"use client";
import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { usePatientDetails } from "../queries/usePatientDetails";
import { usePatientDetailsCardChangedEvent } from "../hooks/usePatientDetailsCardChangedEvent";
import { normalizeCardOnFile } from "@/features/payment/utils/normalizeCardOnFile";
import { Patient } from "../types";
import { CaptureLinkState } from "@/features/card-capture/staff";

interface PatientDetailsContextValue {
  patientId: string | null;
  patient: Patient | undefined;
  isLoading: boolean;
  isError: boolean;
}

const PatientDetailsContext = createContext<PatientDetailsContextValue | null>(
  null,
);

export function PatientDetailsProvider({
  patientId,
  children,
  onError,
  disableCardChangedEvent = false,
}: {
  patientId: string | null;
  children: ReactNode;
  onError?: VoidFunction;
  disableCardChangedEvent?: boolean;
}) {
  const {
    data: patient,
    isLoading,
    isError,
  } = usePatientDetails(patientId, onError);
  usePatientDetailsCardChangedEvent(
    disableCardChangedEvent ? null : patientId,
  );

  return (
    <PatientDetailsContext.Provider
      value={{
        patientId,
        patient: {
          patientId: patient?.patientId ?? "",
          practiceId: patient?.practiceId ?? "",
          firstName: patient?.firstName ?? "",
          middleName: patient?.middleName ?? "",
          lastName: patient?.lastName ?? "",
          dateOfBirth: patient?.dateOfBirth ?? "",
          mrn: patient?.mrn ?? "",
          cardOnFile: normalizeCardOnFile(patient?.cardOnFile),
          cardCapture: (patient?.cardCapture as CaptureLinkState) ?? null,
          phone: patient?.phoneNumber ?? null,
          address: patient?.address,
        },
        isLoading,
        isError,
      }}
    >
      {children}
    </PatientDetailsContext.Provider>
  );
}

export function usePatientDetailsContext() {
  const ctx = useContext(PatientDetailsContext);
  if (!ctx)
    throw new Error(
      "usePatientDetailsContext must be used within PatientDetailsProvider",
    );
  return ctx;
}
